import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemo2Ctx } from '../Demo2Context';
import Demo2ZoneMap from '../Demo2ZoneMap';
import { Demo2ZoneRegForm } from '../panels/Demo2ZoneRegForm';
import { STATUS_LABEL, STATUS_COLOR, TYPE_ICON_RI, treeIconRi, type LatLng, type MoistStatus } from '../demo2Data';
import './Demo2Dashboard.css';

type Trend = 'up' | 'down' | 'flat';

const MAP_STAT_FILTERS: { key: MoistStatus | 'all'; label: string; color: string }[] = [
    { key: 'all', label: '전체', color: 'var(--d-ink2)' },
    { key: 'good', label: '양호', color: '#1E8B53' },
    { key: 'caution', label: '주의', color: '#2563EB' },
    { key: 'warn', label: '위험', color: '#D97706' },
    { key: 'danger', label: '심각', color: '#DC2626' },
];

export default function Demo2Dashboard() {
    const navigate = useNavigate();
    const { zones, alerts, workOrders, workHistory, toast, openPanel } = useDemo2Ctx();
    const [isDrawing, setIsDrawing] = useState(false);
    const [mapStatFilter, setMapStatFilter] = useState<MoistStatus | 'all'>('all');
    const mapZones = mapStatFilter === 'all' ? zones : zones.filter(z => z.status === mapStatFilter);

    const totalTrees = zones.reduce((s, z) => s + z.treeCount, 0);
    const goodCnt = zones.filter(z => z.status === 'good').length;
    const unread = alerts.filter(a => !a.read).length;
    const avgMoist = Math.round(zones.reduce((s, z) => s + z.moist, 0) / zones.length);
    const doneCnt = workOrders.filter(w => w.status === 'done').length;
    const progRate = Math.round((doneCnt / workOrders.length) * 100);

    const KPI: { lbl: string; val: string; ico: string; tone: string; trend: Trend; trendTxt: string }[] = [
        { lbl: '관리구역 수', val: `${zones.length}`, ico: 'ri-map-pin-line', tone: 'blue', trend: 'up', trendTxt: '+1' },
        { lbl: '관리 수목', val: `${totalTrees.toLocaleString()}주`, ico: 'ri-tree-line', tone: 'green', trend: 'up', trendTxt: '+142' },
        { lbl: '오늘의 경고', val: `${unread}`, ico: 'ri-alarm-warning-line', tone: 'red', trend: unread > 0 ? 'down' : 'flat', trendTxt: unread > 0 ? '확인필요' : '정상' },
        { lbl: '수분 양호율', val: `${Math.round((goodCnt / zones.length) * 100)}%`, ico: 'ri-drop-line', tone: 'teal', trend: 'up', trendTxt: '+4%p' },
        { lbl: '관개 진행률', val: `${progRate}%`, ico: 'ri-water-flash-line', tone: 'purple', trend: 'up', trendTxt: '+12%p' },
        { lbl: '평균 수분율', val: `${avgMoist}%`, ico: 'ri-water-percent-line', tone: avgMoist >= 55 ? 'green' : 'orange', trend: avgMoist >= 55 ? 'up' : 'down', trendTxt: avgMoist >= 55 ? '정상' : '주의' },
    ];

    const urgent = zones.filter(z => z.status === 'warn' || z.status === 'danger').sort((a, b) => a.moist - b.moist);

    const handleDrawingComplete = (pts: LatLng[]) => {
        setIsDrawing(false);
        openPanel({
            title: '＋ 신규 관리구역 등록',
            body: <Demo2ZoneRegForm polygon={pts} />,
        });
    };

    return (
        <div className="dash2">
            <div className="dash2-hero">
                <div>
                    <div className="dash2-hero-ttl">
                        수목관리 통합 대시보드
                        <span className="badge-live">LIVE</span>
                    </div>
                    <div className="dash2-hero-sub">전체 관리구역의 생육 · 관개 현황을 실시간으로 확인하세요</div>
                </div>
                <div className="dash2-hero-meta">
                    <span><i className="ri-user-3-line" /> 김관리자</span><span className="dot">·</span><span><i className="ri-calendar-line" /> 2026.07.20 (월) 09:24 기준</span>
                </div>
            </div>

            <div className="dash2-kpi-grid">
                {KPI.map((k, i) => (
                    <div
                        key={i}
                        className="dash2-kpi-card"
                        onClick={() => navigate('/demo2/web/tree-list')}
                    >
                        <div className="dash2-kpi-top">
                            <div className={`d2-ico d2-ico-md d2-ico-${k.tone}`}><i className={k.ico} /></div>
                            <span className="dash2-kpi-lbl">{k.lbl}</span>
                        </div>
                        <div className="dash2-kpi-bottom">
                            <span className="dash2-kpi-val">{k.val}</span>
                            <span className={`dash2-kpi-trend ${k.trend}`}>
                                {k.trend === 'up' ? '▲' : k.trend === 'down' ? '▼' : '●'} {k.trendTxt}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="dash2-main">
                <div className="dash2-card dash2-map-card">
                    <div className="dash2-card-hd">
                        <div>
                            <div className="dash2-card-hd-ttl"><i className="ri-map-pin-line" /> 실시간 관리구역 지도</div>
                            <div className="dash2-card-hd-sub">
                                {isDrawing ? '지도를 클릭해 신규 구역 경계를 그려주세요' : '위성 영상 기반 · 지적경계 오버레이 표시 중'}
                            </div>
                        </div>
                        <div className="dash2-map-hd-right">
                            {!isDrawing && (
                                <div className="dash2-map-filter">
                                    {MAP_STAT_FILTERS.map(f => (
                                        <button
                                            key={f.key}
                                            className={`dash2-map-filter-chip${mapStatFilter === f.key ? ' active' : ''}`}
                                            style={{ '--chip-color': f.color } as CSSProperties}
                                            onClick={() => setMapStatFilter(f.key)}
                                        >
                                            {f.label}
                                            {f.key !== 'all' && <span className="dash2-map-filter-cnt">{zones.filter(z => z.status === f.key).length}</span>}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {isDrawing ? (
                                <button className="dash2-btn" style={{ color: 'var(--danger)', borderColor: '#FECACA' }} onClick={() => setIsDrawing(false)}><i className="ri-close-line" /> 그리기 취소</button>
                            ) : (
                                <button className="dash2-btn dash2-btn-primary" onClick={() => setIsDrawing(true)}>＋ 관리구역 등록</button>
                            )}
                        </div>
                    </div>
                    <div className="dash2-map-body">
                        <Demo2ZoneMap
                            zones={zones}
                            visibleIds={mapStatFilter === 'all' ? undefined : mapZones.map(z => z.id)}
                            zoom={16}
                            mapTypeToggle
                            defaultMapType="SATELLITE"
                            showWeather
                            drawingMode={isDrawing}
                            onDrawingComplete={handleDrawingComplete}
                            onSelectZone={id => {
                                const z = zones.find(zz => zz.id === id);
                                if (z) toast(`${z.icon} ${z.name} · 수분 ${z.moist}% (${STATUS_LABEL[z.status]}) · 담당 ${z.mgr}`, z.status === 'danger' || z.status === 'warn' ? 'warning' : 'success');
                            }}
                        />
                        <div className="dash2-legend-glass">
                            <div className="dash2-legend-ttl">수분 상태</div>
                            <div className="dash2-legend-row"><span className="dash2-legend-dot" style={{ background: '#1E8B53' }} />양호 70%↑</div>
                            <div className="dash2-legend-row"><span className="dash2-legend-dot" style={{ background: '#2563EB' }} />주의 45~69%</div>
                            <div className="dash2-legend-row"><span className="dash2-legend-dot" style={{ background: '#D97706' }} />위험 25~44%</div>
                            <div className="dash2-legend-row"><span className="dash2-legend-dot" style={{ background: '#DC2626' }} />심각 25%↓</div>
                        </div>
                    </div>
                </div>

                <div className="dash2-card dash2-alert-card">
                    <div className="dash2-card-hd">
                        <div className="dash2-card-hd-ttl"><i className="ri-alarm-warning-line" /> 관개 필요 구역</div>
                        <span className="dash2-alert-count">{urgent.length}</span>
                    </div>
                    <div className="dash2-alert-list">
                        {urgent.length === 0 && (
                            <div className="d2-empty">
                                <i className="ri-checkbox-circle-line" />
                                <div className="d2-empty-txt">현재 관개가 필요한 구역이 없습니다</div>
                                <div className="d2-empty-sub">모든 구역이 양호한 수분 상태를 유지하고 있어요</div>
                            </div>
                        )}
                        {urgent.map(z => (
                            <div
                                key={z.id}
                                className="dash2-alert-item"
                                style={{ '--al-color': STATUS_COLOR[z.status] } as CSSProperties}
                                onClick={() => navigate('/demo2/web/map')}
                            >
                                <div className="dash2-al-top">
                                    <span className="dash2-al-name"><i className={treeIconRi(z.treeName)} /> {z.name}</span>
                                    <span className="dash2-al-pill" style={{ '--al-color': STATUS_COLOR[z.status], '--al-bg': `${STATUS_COLOR[z.status]}1A` } as CSSProperties}>{STATUS_LABEL[z.status]}</span>
                                </div>
                                <div className="dash2-al-meta"><span><i className="ri-user-3-line" /> {z.mgr}</span><span><i className="ri-calendar-line" /> 마지막 관개 {z.lastDate}</span></div>
                                <div className="dash2-al-stats">
                                    <div className="dash2-al-stat"><div className="dash2-al-stat-lbl">현재 수분율</div><div className="dash2-al-stat-val" style={{ color: STATUS_COLOR[z.status] }}>{z.moist}%</div></div>
                                    <div className="dash2-al-stat"><div className="dash2-al-stat-lbl">구분</div><div className="dash2-al-stat-val" style={{ fontSize: 12.5 }}>{z.category}</div></div>
                                </div>
                                <button className="dash2-al-cta" onClick={e => { e.stopPropagation(); navigate('/demo2/web/work-management'); }}><i className="ri-drop-line" /> 관개 작업지시 생성</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="dash2-card dash2-recent">
                <div className="dash2-recent-hd">
                    <div className="dash2-card-hd-ttl"><i className="ri-history-line" /> 최근 작업 이력</div>
                    <span className="dash2-recent-link" onClick={() => navigate('/demo2/web/work-history')}>전체 보기 <i className="ri-arrow-right-line" /></span>
                </div>
                <div className="dash2-recent-row">
                    {workHistory.slice(0, 6).map((h, i) => (
                        <div key={i} className="dash2-recent-card">
                            <div className="dash2-rc-top">
                                <div className="d2-ico d2-ico-sm d2-ico-green"><i className={TYPE_ICON_RI[h.type] || 'ri-file-list-3-line'} /></div>
                                <span className="dash2-rc-type">{h.type}</span>
                                <span className="dash2-rc-time">{h.date.slice(5, 16)}</span>
                            </div>
                            <div className="dash2-rc-zone">{h.zoneName}</div>
                            <div className="dash2-rc-bottom">
                                <span><i className="ri-user-3-line" /> {h.worker}</span>
                                <span className="dash2-rc-delta" style={{ color: 'var(--good)' }}>{h.before}%→{h.after}%</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
